import type { MediaTypeFilter, TimelineFilter, UploaderFilter } from "./media/timeline";

/**
 * React Query のキーを1箇所に集める。
 *
 * 画面ごとに文字列リテラルを書くと、invalidate が「たまたま同じ綴りだった」
 * ことに依存して壊れやすい。特にお気に入り画面はタイムラインと同じキー構造を
 * 使うことで invalidation を共有しており、この結合はここで明示しておく。
 */
export const queryKeys = {
  /** 全フィルタ・全起点のタイムラインをまとめて invalidate する接頭辞 */
  timelines: ["timeline"] as const,
  timeline: (
    filter: TimelineFilter,
    type: MediaTypeFilter,
    month: string | null,
    uploader: UploaderFilter,
  ) => ["timeline", filter, type, month, uploader] as const,
  /** timelines の接頭辞に載せて、一覧の invalidate で件数も取り直す */
  timelineCount: (
    filter: TimelineFilter,
    type: MediaTypeFilter,
    month: string | null,
    uploader: UploaderFilter,
  ) => ["timeline", "count", filter, type, month, uploader] as const,
  monthCounts: ["month-counts"] as const,
  tags: ["tags"] as const,
  favorites: ["favorites"] as const,
  /** お気に入り一覧(追加順)と総数。favorites の接頭辞に載せ、登録・解除の invalidate で取り直す */
  favoritesList: ["favorites", "list"] as const,
  favoritesCount: ["favorites", "count"] as const,
  members: ["members"] as const,
  trash: ["trash"] as const,
  events: ["events"] as const,
  /** 繰り返し予定の「この回だけ」の例外(キャンセル・差し替え) */
  eventExceptions: ["event-exceptions"] as const,
  eventCategories: ["event-categories"] as const,
  anniversaries: ["anniversaries"] as const,
  comments: (mediaId: string | undefined) => ["comments", mediaId] as const,
  /** 原本の署名付きURL。キー単位で持ち、失効したものだけ取り直す */
  mediaUrl: (storageKey: string | undefined) => ["media-url", storageKey] as const,
  profile: (userId: string | undefined) => ["profile", userId] as const,
  /** カップル二人ぶんのプロフィール(誕生日を含む)。設定・ふたりごとから使う */
  birthdays: ["birthdays"] as const,
  diaryEntries: ["diary-entries"] as const,
  /** お題は共通のマスタデータなので couple では分けない */
  diaryPrompts: ["diary-prompts"] as const,
  /** ふたりごとの任意項目とその答え */
  profileItems: ["profile-items"] as const,
  profileAnswers: ["profile-answers"] as const,
  /** ふたりぶんのプロフィール全体(連絡先・アイコン込み) */
  coupleProfiles: ["couple-profiles"] as const,
  /** 保存中メディアの件数と容量(設定画面の確認用) */
  mediaUsage: ["media-usage"] as const,
  /** 使用量とカップルごとの上限(残量表示・容量制限) */
  storageStatus: ["storage-status"] as const,
  /** メニュー(タブ)のユーザーごとの設定 */
  navPrefs: ["nav-prefs"] as const,
  /** メディアごとのコメント件数(タイル・詳細のバッジ用) */
  commentCounts: ["comment-counts"] as const,
  /** 日記のしおり。挟む・外すの invalidate はこの接頭辞をまとめて使う */
  diaryBookmarks: ["diary-bookmarks"] as const,
  diaryBookmarkIds: ["diary-bookmarks", "ids"] as const,
  /** 「二人のベストセレクション」。diaryBookmarks の接頭辞に載せ、挟む・外すの invalidate で取り直す */
  diaryMutualBookmarkIds: ["diary-bookmarks", "mutual"] as const,
  /** 日記エントリーごとの添付画像ID */
  diaryEntryMedia: ["diary-entry-media"] as const,
  /** 自分が最後に交換日記を書いた日時。「今日ぶんが未登録か」のリマインダー表示に使う */
  diaryLatestOwn: (authorId: string) => ["diary-latest-own", authorId] as const,
  /** 詳細の情報パネル用のメタ情報(形式・大きさ・追加日時) */
  mediaMeta: (mediaId: string) => ["media-meta", mediaId] as const,
  /** 一括付与の面で使う、選んだメディアのタグごとの付与件数 */
  mediaTagCounts: (mediaIds: string[]) => ["media-tag-counts", mediaIds] as const,
  /** ふりかえりメモのノート一覧 */
  reflectionNotes: ["reflection-notes"] as const,
  /** ふりかえりメモの各自の振り返り(couple全体ぶん) */
  reflectionEntries: ["reflection-entries"] as const,
  /** 共有リストの「行ってみたい場所」「行った場所」 */
  places: ["places"] as const,
  /** メディアIDからサムネイルのキーを引く(中間テーブル経由でIDしか無い面向け) */
  mediaThumbnails: (mediaIds: string[]) => ["media-thumbnails", mediaIds] as const,
  /** ダイエットの日々の記録(二人ぶん) */
  dietLogs: ["diet-logs"] as const,
  /** ダイエットの目標(二人ぶん)。決めていない人の行は無い */
  dietGoals: ["diet-goals"] as const,
  /** 応援スタンプ。押す・取り消すたびに取り直す */
  dietCheers: ["diet-cheers"] as const,
  /** ふたりのごほうび(couple に1つ) */
  dietReward: ["diet-reward"] as const,
};
